import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { HolidayService } from '@service/holiday/holiday.service';

export interface HolidayRowModel {
  fromDate: Date | null;
  toDate: Date | null;
  eventId: number | string | null;
  eventReason?: string;
}

export interface EventReasonOption {
  id: number | string;
  name: string;
}

@Component({
  selector: 'app-add-holiday-modal',
  templateUrl: './add-holiday-modal.component.html',
  styleUrls: ['./add-holiday-modal.component.css'],
})
export class AddHolidayModalComponent implements OnInit {
  @Input() isPopupVisible: boolean = false;
  @Input() holidayYear: number;
  @Output() isPopupVisibleChange = new EventEmitter<boolean>();

  @Output() onSaveHolidays = new EventEmitter<HolidayRowModel[]>();

  minDate: Date = new Date();

  holidayRows: HolidayRowModel[] = [];
  holidayEvents: any[] = [];
  /** Render pickers on body so they are not clipped by modal overflow */
  editorDropDownOptions = { container: 'body' };

  constructor(private readonly holidayService: HolidayService) {}

  ngOnInit(): void {
    this.getAllEvent();

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow;

    this.resetForm();
  }

  getAllEvent(): void {
    this.holidayService.getAllHolidayEvent().subscribe(
      (response) => {
        if (response && response.status === 'ok') {
          this.holidayEvents = response.resultset.filter(
            (event: any) => event.name !== 'Weekend',
          );
        } else {
          console.error('Failed to fetch holiday events:', response);
        }
      },
      (error) => {
        console.error('Error fetching holiday events:', error);
      },
    );
  }

  addNewRow(): void {
    this.holidayRows.push({
      fromDate: null,
      toDate: null,
      eventId: null,
      eventReason: '',
    });
  }

  removeRow(index: number): void {
    if (this.holidayRows.length > 1) {
      this.holidayRows.splice(index, 1);
    }
  }

  resetForm(): void {
    this.holidayRows = [
      { fromDate: null, toDate: null, eventId: null, eventReason: '' },
    ];
  }

  closePopup(): void {
    this.resetForm();
    this.isPopupVisible = false;
    this.isPopupVisibleChange.emit(this.isPopupVisible);
  }

  saveAllHolidays(): void {
    const validRows = this.holidayRows.filter(
      (row) => row.fromDate && row.toDate && row.eventId,
    );

    if (validRows.length === 0) {
      alert('Please fill up at least one complete holiday row.');
      return;
    }

    // Validate Others reason
    const invalidOthersRow = validRows.find(
      (row) => this.isOthersEvent(row.eventId) && !row.eventReason?.trim(),
    );

    if (invalidOthersRow) {
      alert('Please enter the event reason for Others.');
      return;
    }

    // Validate date range
    const invalidDateRow = validRows.find(
      (row) => new Date(row.fromDate!) > new Date(row.toDate!),
    );

    if (invalidDateRow) {
      alert('To Date must be greater than or equal to From Date.');
      return;
    }

    const payload: {
      list: any[];
    } = {
      list: [],
    };

    validRows.forEach((row) => {
      const currentDate = new Date(row.fromDate!);
      const endDate = new Date(row.toDate!);

      while (currentDate <= endDate) {
        const holiday: any = {
          holiday_year: this.holidayYear,
          holiday_event_id: row.eventId,
          date: new Date(
            Date.UTC(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
              12,
              0,
              0,
              0,
            ),
          ).toISOString(),
        };

        // Add event_reason only for Others
        if (this.isOthersEvent(row.eventId)) {
          holiday.event_reason = row.eventReason!.trim();
        }

        payload.list.push(holiday);

        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    this.holidayService.createHoliday(payload).subscribe(
      (response) => {
        if (response && response.status === 'ok') {
          this.onSaveHolidays.emit(response.resultset);
          this.closePopup();
        } else {
          console.error('Failed to save holidays:', response);
        }
      },
      (error) => {
        console.error('Error saving holidays:', error);
      },
    );
  }

  isOthersEvent(eventId: number | string | null): boolean {
    if (eventId === null || eventId === undefined) {
      return false;
    }

    const selectedEvent = this.holidayEvents.find(
      (event: any) => String(event.id) === String(eventId),
    );

    return selectedEvent?.name?.trim().toLowerCase() === 'others';
  }

  onEventChanged(event: any, row: HolidayRowModel): void {
    if (!this.isOthersEvent(event.value)) {
      row.eventReason = '';
    }
  }
}
